import { useEffect, useRef, useState } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  DEFAULT_DECK_VIEW_INDEX,
  DEFAULT_VIEW_INDEX,
  DeckViewIndex,
  ViewIndex,
} from "modules/common/model/viewTypes";
import {
  hasViewPreferenceCookie,
  setViewPreferenceCookie,
} from "utils/view-preference";

interface CommonStore {
  // Search state
  searchOpen: boolean;
  setSearchOpen: (open: boolean) => void;
  toggleSearch: () => void;

  // Unified view preference (Table=0, Visual=1)
  // Used as default across Collections, Trade Lists, LGS
  viewPreference: ViewIndex;
  setViewPreference: (view: ViewIndex) => void;

  // Deck view preference (0=Table, 1=VisualStack, 2=VisualSpoiler, 3=VisualGrid, 4=VisualSplit)
  // Separate from main viewPreference because decks use different view modes
  deckViewPreference: DeckViewIndex;
  setDeckViewPreference: (view: DeckViewIndex) => void;

  // Printings view preference (Table=0, Visual=1)
  // Separate from main viewPreference so the card details printings panel
  // doesn't affect other views
  printingsViewPreference: ViewIndex;
  setPrintingsViewPreference: (view: ViewIndex) => void;

  // Reset action
  reset: () => void;
}

export const useCommonStore = create<CommonStore>()(
  persist(
    (set, get) => ({
      // Initial state
      searchOpen: false,
      viewPreference: DEFAULT_VIEW_INDEX,
      deckViewPreference: DEFAULT_DECK_VIEW_INDEX,
      printingsViewPreference: DEFAULT_VIEW_INDEX,

      // Search actions
      setSearchOpen: (open) => set({ searchOpen: open }),
      toggleSearch: () => set({ searchOpen: !get().searchOpen }),

      // View preference actions
      setViewPreference: (view) => {
        set({ viewPreference: view });
        setViewPreferenceCookie(view);
      },

      // Deck view preference actions
      setDeckViewPreference: (view) => set({ deckViewPreference: view }),

      // Printings view preference actions
      setPrintingsViewPreference: (view) => set({ printingsViewPreference: view }),

      // Reset action
      reset: () =>
        set({
          searchOpen: false,
          viewPreference: DEFAULT_VIEW_INDEX,
          deckViewPreference: DEFAULT_DECK_VIEW_INDEX,
          printingsViewPreference: DEFAULT_VIEW_INDEX,
        }),
    }),
    {
      name: "lootstack-view-preferences",
      partialize: (state) => ({
        viewPreference: state.viewPreference,
        deckViewPreference: state.deckViewPreference,
        printingsViewPreference: state.printingsViewPreference,
      }),
    }
  )
);

// ============================================================================
// HYDRATION HOOK - Detects when Zustand persist has finished reading localStorage
// ============================================================================

/**
 * Returns true once Zustand persist middleware has finished rehydrating from
 * localStorage. Uses the built-in `hasHydrated()` + `onFinishHydration()` API
 * so components can avoid rendering stale default values during SSR → client
 * handoff.
 *
 * Also syncs localStorage → cookie on first hydration so existing users get a
 * cookie without needing to manually change their view preference.
 */
export const useStoreHydrated = () => {
  // The server snapshot and the first client render must agree. Persist can
  // finish before React hydrates, so reading hasHydrated() in the initializer
  // makes the client select a different view branch than the server.
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    // One-time migration: sync localStorage preference → cookie for existing users
    // who have a localStorage value but no cookie yet. Once the cookie exists,
    // setViewPreference() keeps them in sync going forward.
    const syncCookieIfMissing = (viewPreference: 0 | 1) => {
      if (!hasViewPreferenceCookie()) {
        setViewPreferenceCookie(viewPreference);
      }
    };

    // If already hydrated before this effect ran, sync immediately.
    if (useCommonStore.persist?.hasHydrated()) {
      setHydrated(true);
      syncCookieIfMissing(useCommonStore.getState().viewPreference);
      return;
    }

    const unsub = useCommonStore.persist?.onFinishHydration((state) => {
      setHydrated(true);
      syncCookieIfMissing(state.viewPreference);
    });
    return () => unsub?.();
  }, []);

  return hydrated;
};

// ============================================================================
// SELECTOR HOOKS - Optimal Zustand patterns with individual selectors
// ============================================================================

/**
 * Search state selector - for search button and overlay components
 */
export const useSearch = () => {
  const searchOpen = useCommonStore((state) => state.searchOpen);
  const setSearchOpen = useCommonStore((state) => state.setSearchOpen);
  const toggleSearch = useCommonStore((state) => state.toggleSearch);

  return { searchOpen, setSearchOpen, toggleSearch };
};

/**
 * View preference selector - for Table/Visual view switching.
 *
 * Pass `initialViewIndex` (read from a server cookie) to get the correct view
 * on the very first render without a flash, while still letting the store be
 * the single source of truth after mount.
 *
 * Strategy:
 *  1. SSR / first client render  → returns `initialViewIndex ?? viewPreference`
 *     so there is no flash of the wrong view.
 *  2. After mount (useEffect)    → seeds the store via setState (no cookie
 *     side-effect) so the store matches the server value.
 *  3. User changes view          → setViewPreference() updates the store AND
 *     writes the cookie correctly, as it always did.
 *
 * When called without an argument it behaves exactly like before.
 */
export const useViewPreference = (initialViewIndex?: ViewIndex) => {
  const viewPreference = useCommonStore((state) => state.viewPreference);
  const setViewPreference = useCommonStore((state) => state.setViewPreference);

  const seeded = useRef(false);
  useEffect(() => {
    if (initialViewIndex !== undefined) {
      useCommonStore.setState({ viewPreference: initialViewIndex });
    }
    seeded.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const viewIndex: ViewIndex = seeded.current
    ? viewPreference
    : (initialViewIndex ?? viewPreference);

  return { viewPreference: viewIndex, setViewPreference };
};

/**
 * Printings view preference selector - for Table/Visual switching in
 * the card details printings panel, independent from the main view preference.
 */
export const usePrintingsViewPreference = () => {
  const printingsViewPreference = useCommonStore(
    (state) => state.printingsViewPreference
  );
  const setPrintingsViewPreference = useCommonStore(
    (state) => state.setPrintingsViewPreference
  );

  return { printingsViewPreference, setPrintingsViewPreference };
};

/**
 * Deck view preference selector - for Table/VisualStack view switching in decks
 * Separate from main viewPreference because decks have different view modes
 */
export const useDeckViewPreference = () => {
  const deckViewPreference = useCommonStore(
    (state) => state.deckViewPreference
  );
  const setDeckViewPreference = useCommonStore(
    (state) => state.setDeckViewPreference
  );

  return { deckViewPreference, setDeckViewPreference };
};
